import { Injectable } from '@angular/core';
import { Collection, Feature } from 'ol';
import { Coordinate } from 'ol/coordinate';
import { never, primaryAction } from 'ol/events/condition';
import { Geometry, MultiPoint, Point, Polygon, SimpleGeometry } from 'ol/geom';
import { Extent, Interaction, Modify, Translate } from 'ol/interaction';
import { ModifyEvent } from 'ol/interaction/Modify';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import { take } from 'rxjs/operators';
import { getCollectionName } from '../../home/object-info/object-utils';
import { Collection as namCollection } from 'src/app/home/object-info/model/config-api-response.interface';
import { MappaService } from '../mappa/mappa.service';
import { GeometryUtilsService } from './geometry-utils.service';
import { NamSelectedObjectService } from './nam-object-selected.service';
import { NamService } from './nam.service';
import { ProjectService } from './project.service';
import { WorldService } from './world.service';
import { RelatedItemsInterface } from 'src/app/home/object-info/model/related-item.interface';
import { EbwModalService } from 'src/app/library/ebw-modals-angular/ebw-modal.service';
import { fromExtent } from 'ol/geom/Polygon';

@Injectable({
  providedIn: 'root'
})
export class EditFeatureService {

  public editingGeometry: boolean = false;
  modifyGeometry: Geometry;
  translateInteraction: Interaction;
  rotateInteraction: Interaction;
  totalRotation = 0;
  mainVectorLayer: VectorLayer;
  mainVectorSource: VectorSource;
  mainFeature: Feature<Polygon>;
  
  // Copy of templateFeature, used to implement geometry snap.
  ghostVectorLayer: VectorLayer;
  ghostVectorSource: VectorSource;
  ghostFeature: Feature<Polygon>;

  featureUrn: string;

  worldRelatedUrns: string[];

  objectCollection: namCollection;

  parent: any;
  relatedPolygons: any[] = []

  geometryIsValid = false;
  INVALID_GEOM_COLOR = "#FF5959";
  VALID_GEOM_COLOR = "#5C9AFF";

  snap: boolean = false;

  constructor(
    private mapService: MappaService,
    private projectService: ProjectService,
    private worldService: WorldService,
    private namService: NamService,
    private geometryUtils: GeometryUtilsService,
    private namSelectedObjectService: NamSelectedObjectService,
    private ebwModalService: EbwModalService
  ) {
    this.worldService.onChange.subscribe((worldInfo) => {
      this.cancelEditGeometry();
    });
  }

  //Properties must be a JSON string
  async sendEditGeometry(){    
    let boundingBox = this.mainFeature.getGeometry().getExtent();
    
    let rotation = this.totalRotation;
    let world = this.worldService.getWorldInfo().world_urn;
    let projectId = await this.projectService.getProject();

    this.validateCurrentGeometry();
    
    if(this.geometryIsValid){
      let urn = this.featureUrn;
      this.namService.moveFeatureEdit(boundingBox, world, urn, projectId)
      .pipe(take(1))
      .subscribe( dataMove => {
        this.namService.rotateFeatureEdit(rotation, world, urn, projectId)
        .pipe(take(1))
        .subscribe( dataRotate => {
          //only reset when both operations succeded
          this.worldService.updateWorldMap();
          this.namSelectedObjectService.deselectedObject = null;
          this.resetState();
        });
      });
    } else {
      //TODO: Send error message
      this.ebwModalService.openErrorModal(
        {
          title: "Errore in modifica geometria", 
          content: "L'oggetto modificato si interseca con un oggetto esistente."
        }
      );
    }
  }

  isSnapActive(): boolean {
    return this.snap;
  }

  toggleSnap() {
    this.snap = !this.snap;
  }

  cancelEditGeometry() {
    this.mapService.mappa.mappaOpenLayer.removeLayer(this.mainVectorLayer);
    this.namSelectedObjectService.deselectedObject = null;
    this.resetState();
  }

  validateCurrentGeometry() {
    this.geometryIsValid = true;

    // Make sure the current geometry lies within its parent.
    // if (this.parent) {
    //   this.geometryIsValid &&= this.geometryUtils.checkContains(this.objectFeature.getGeometry().getCoordinates(), this.parent);
    // }

    // Make sure the current geometry does not overlap its siblings.
    this.geometryIsValid &&= !(this.geometryUtils.checkOverlaps(this.mainFeature.getGeometry().getCoordinates() , this.relatedPolygons));
  }

  resetState(){
    this.mapService.mappa.mappaOpenLayer.removeInteraction(this.translateInteraction);
    this.mapService.mappa.mappaOpenLayer.removeInteraction(this.rotateInteraction);
    this.mapService.mappa.mappaOpenLayer.removeLayer(this.ghostVectorLayer);
    this.mapService.mappa.mappaOpenLayer.removeLayer(this.mainVectorLayer);

    this.modifyGeometry = null;
    this.editingGeometry = false;
    this.mainFeature = null;
    this.ghostFeature = null;
    this.mainVectorLayer = null;
    this.mainVectorSource = null;
    this.ghostVectorLayer = null;
    this.ghostVectorSource = null;
    this.translateInteraction = null;
    this.rotateInteraction = null;
    this.totalRotation = 0;
    this.featureUrn = null;
    this.worldRelatedUrns = [];
    this.geometryIsValid = false;
    this.snap = false;
  }

  /**
   * Adds the polygon object to the map. 
   * 
   * It adds the rotate and translate interactions when the polygon is added.
   * @param object Nam object object
   */
  async addEditGeometryToMap(object: any){
    // Only add object if there are no objects being edited.
    if (!this.editingGeometry) {
      let objectFeature = object.result.features[0];
      
      //Ensure that a project exists when adding the object
      this.projectService.getProject();
      this.featureUrn = objectFeature.id;

      let mapObject = this.mapService.mappa.mappaOpenLayer;
      let collection = getCollectionName(objectFeature.id);
      
      let objectPolygon = new Polygon(objectFeature.geometry.coordinates);
      this.mainFeature = new Feature(fromExtent(objectPolygon.getExtent()));
      this.ghostFeature = new Feature(fromExtent(objectPolygon.getExtent()));
      this.mainVectorSource = new VectorSource();
      this.ghostVectorSource = new VectorSource();
      this.mainVectorSource.addFeature(this.mainFeature);
      this.ghostVectorSource.addFeature(this.ghostFeature);
      const bbox = objectPolygon.getExtent(); //[minx, miny, maxx, maxy].

      const xmin = bbox[0];
      const ymin = bbox[1];

      const x = objectPolygon.getCoordinates()[0].find( (coord: Coordinate) => {
        return coord[0] == xmin && coord[1] != ymin;
      } )[0];

      const y = objectPolygon.getCoordinates()[0].find( (coord: Coordinate) => {
        return coord[0] != xmin && coord[1] == ymin;
      } )[1];

      //tan alpha = opposite / adjacent
      const opposite = y - ymin;
      const adjacent = x - xmin;

      const initialAngle = Math.atan2(opposite, adjacent); //in radians
      this.totalRotation = this.geometryUtils.rad2deg(initialAngle);

      this.editingGeometry = true;

      this.mainVectorLayer = new VectorLayer({
        source: this.mainVectorSource,
        style: (feature: Feature) => {
          let styles = [
            new Style({
              //Takes the style geometry of the modifyGeometry if the object is being modified.
              geometry: (feature: Feature) => {             
                const modifyGeometry: {geometry: Geometry} = feature.get('modifyGeometry');
                return modifyGeometry ? modifyGeometry.geometry : feature.getGeometry();
              },

              fill: new Fill({
                color: "transparent"
              }),
    
              stroke: new Stroke({
                color: this.geometryIsValid ? this.VALID_GEOM_COLOR : this.INVALID_GEOM_COLOR,
                width: 3
              }),
            })
          ];
          
          //The following is to create the points on the corners of the polygon.
          let modifyGeometry: {geometry: Geometry} = feature.get('modifyGeometry');
          let geometry = modifyGeometry
            ? modifyGeometry.geometry
            : feature.getGeometry();

          let featureCoordinates = this.geometryUtils.calculateCenter(geometry);
          const coordinates = featureCoordinates.coordinates;
          if (coordinates) {
            const points = coordinates;
            const center = featureCoordinates.center;

            if (center) {
              styles.push(
                new Style({
                  geometry: new Point(center),
                  image: new CircleStyle({
                    radius: 4,
                    fill: new Fill({
                      color: '#28b800',
                    }),
                  }),
                })
              );
            }

            //Add the corners to the style function.
            styles.push(
              new Style({
                geometry: new MultiPoint(points),
                image: new CircleStyle({
                  radius: 4,
                  fill: new Fill({
                    color: '#28b800',
                  }),
                }),
              })
            );
          }
          return styles;
        },
        visible: true
      });

      this.ghostVectorLayer = new VectorLayer({
        source: this.ghostVectorSource,
        style: (feature: Feature) => {
          let styles = [
            new Style({
              //Takes the style geometry of the modifyGeometry if the object is being modified.
              geometry: (feature: Feature) => {             
                const modifyGeometry: {geometry: Geometry} = feature.get('modifyGeometry');
                return modifyGeometry ? modifyGeometry.geometry : feature.getGeometry();
              },

              fill: new Fill({
                color: "transparent"
              }),
    
              stroke: new Stroke({
                color: "#55555544",
                width: 3
              }),
            })
          ];
          return styles;
        },
        visible: true
      });

      //adds the layer with the polygon to the map.
      mapObject.addLayer(this.ghostVectorLayer);
      mapObject.addLayer(this.mainVectorLayer);

      //Fit view to layer
      this.mapService.mappa.mappaOpenLayer.getView().fit(this.mainFeature.getGeometry() as SimpleGeometry,{maxZoom: 5, duration:500});

      //This is to generate a default style of the Modify interaction.
      //This is done to create a parametrical style that can be used by the style funcion of the Modify interaction.
      let defaultStyle = new Modify({source: this.mainVectorSource})
      .getOverlay()
      .getStyleFunction();

      //Globaly create the translate interaction so it can be removed later.
      this.translateInteraction = new Translate({
        features: new Collection([this.ghostFeature])
      });

      //Check is done on the translating event because the translateend does not change the geometry
      this.translateInteraction.on('translating', (event) => {
        if (this.snap) {
          // Flatten all related polygons (siblings).
          let snapPoints: Array<Array<number>> = this.relatedPolygons.reduce(
            (prev: Array<Array<number>>, curr: Array<Array<number>>) => prev.concat(curr.reduce(
                (in_prev: Array<number>, in_curr: Array<number>) => in_prev.concat(in_curr)
              ))
          )
          
          let uniqueSnapPoints: Array<Array<number>> = snapPoints.filter(
            (value: Array<number>, index: number) => snapPoints.indexOf(value) === index
          );

          // If there's any corner closer than the defined tolerance, then snap to the closest one.
          let minDist: number = 1e15;
          let minDistX: number = 1e15;
          let minDistY: number = 1e15;
          
          this.ghostFeature.getGeometry().getCoordinates()[0].forEach(point => {
            uniqueSnapPoints.forEach(snapPoint => {
              let distComponents = this.geometryUtils.pointDistance(snapPoint, point);
              // let distComponents = this.geometryUtils.sqrDistance(snapPoint, featureExtent);
              let dist = Math.pow(distComponents[0], 2) + Math.pow(distComponents[1], 2);
              if (dist < minDist) {
                minDist = dist;
                minDistX = distComponents[0];
                minDistY = distComponents[1];
              }
            });
          })
          this.mainFeature.getGeometry().setCoordinates(this.ghostFeature.getGeometry().getCoordinates())
          if (minDist < 1e9) {
            this.mainFeature.getGeometry().translate(minDistX, minDistY)
          }
        } else {
          this.mainFeature.getGeometry().setCoordinates(this.ghostFeature.getGeometry().getCoordinates())
        }

        //Check for overlaps
        this.validateCurrentGeometry();
      });

      //Globally create the rotate interaction so it can be removed later.
      this.rotateInteraction = new Modify({
        source: this.mainVectorSource, //The global vector of the layer being added
        condition: function (event) {
          return primaryAction(event);
        },
        deleteCondition: never, //This prevents the modify default option to delete a vertex
        insertVertexCondition: never, //This prevents the modify default option to insert a vertex
        pixelTolerance: 5,

        //The style function is where the magic happens.
        //It uses the original geometry being modified, the geometry with the latest modifications,
        // the initial point that was clicked to start the rotation, and the current rotation angle.
        //All those values are added at the modifystart event and removed at the modifyend event.
        //This creates something like a "session" of rotation, and the final geometry of the object
        //becomes the geometry after the modifications.
        style: (feature: Feature<Point>) => { 
          let modifyGeometry: {geometry: Geometry} = this.mainFeature.get('modifyGeometry');

          //If modifyGeometry exists the feature is being modified.
          if(modifyGeometry){

            //Create a clone of the original geometry to rotate it. It perfoms rotation on the original
            //  geometry to do a smooth rotation and record the final rotation angle.
            let originalGeometryClone: Geometry = this.mainFeature.get('originalGeometry').geometry.clone();

            //This is the current pointer location in the form of a Point feature.
            let modifyPoint = feature.getGeometry().getCoordinates();          
            let originalPoint: {coordinate: Coordinate} = this.mainFeature.get('originalPointCoordinates');

            //Coordinates of the "original" point that is being modified.
            let pointBeingModified = originalPoint.coordinate;
          
            //Calculate the center of the original geometry to use it as pivot.
            const result = this.geometryUtils.calculateCenter(originalGeometryClone);
            const trueCenter = result.center;

            //To get the angle between the point modified and the original point being modified, it creates a new "center"
            // that is the X component of the true center and the Y component of the original point being modified.
            let angleCenter = [trueCenter[0], pointBeingModified[1]];

            //Calculate the triangle 
            let dx = modifyPoint[0] - angleCenter[0];
            let dy = modifyPoint[1] - angleCenter[1];

            //Calculate the angle of rotation 
            //WARNING: THIS IS IN RADIANS
            let angle = Math.atan2(dy, dx);

            //Rotate the original geometry with the true center as the pivot.
            originalGeometryClone.rotate(angle, trueCenter);

            //Sets the modifyGeometry as the geometry rotated
            this.mainFeature.set('modifyGeometry', { geometry: originalGeometryClone });
            //Sets the total angle rotation so it is stored.
            this.mainFeature.set('rotationAngle', { angle: angle });
          }

          return defaultStyle(feature, 0);
        },
      });

      this.rotateInteraction.on('modifystart', (event: ModifyEvent) => {      
        //Store rotation "session" variables on the modified feature.
        let originalPoint = event.mapBrowserEvent.coordinate;
        this.mainFeature.set('modifyGeometry', { geometry: this.mainFeature.getGeometry().clone() }, true);
        this.mainFeature.set('originalGeometry', { geometry: this.mainFeature.getGeometry().clone() }, true)
        this.mainFeature.set('originalPointCoordinates', { coordinate: originalPoint }, true);
        this.mainFeature.set('rotationAngle', { angle: this.totalRotation }, true);
      });

      this.rotateInteraction.on('modifyend', (event: ModifyEvent) => {
        //Check if the modifyGeometry exists
        let modifyGeometry = this.mainFeature.get('modifyGeometry');

        //Destroy the rotation "session" variables and sets the global total rotation as the total angle,
        // and the object geometry as the last modifyGeometry. 
        if (modifyGeometry) {
          let angle = this.mainFeature.get('rotationAngle').angle;
          
          this.totalRotation += this.geometryUtils.rad2deg(angle);
          //removes any overflow of the total angle
          if(this.totalRotation > 360) this.totalRotation =- 360;
          if(this.totalRotation < 0) this.totalRotation += 360;

          this.mainFeature.setGeometry(modifyGeometry.geometry);
          this.mainFeature.unset("modifyGeometry", true);
          this.mainFeature.unset("originalGeometry", true);
          this.mainFeature.unset("originalPointCoordinates", true);
          this.mainFeature.unset("rotationAngle", true);

          //Check for overlaps
          this.validateCurrentGeometry();
        }
        
      });

      //Add the interactions
      try {
        mapObject.addInteraction(this.translateInteraction);
        if(collection == "mit_bay"){
          mapObject.addInteraction(this.rotateInteraction);
        }
      } catch(error) {
        console.log("ERROR NINNO", error);
      }

      // Fetch current feature's container.
      let parentUrn;
      await this.namSelectedObjectService.selectedObjectForInfo$.pipe(take(1)).subscribe(value => parentUrn = value.urn);
      console.log("DE_CURRENT_URN", parentUrn);
      let parentPromise = this.namService.getSelectByUrns([parentUrn]).pipe(take(1)).toPromise();

      await parentPromise.then(data => {
        this.parent = data.result.features.filter((element) => {
          return element["geometry"]["type"] == "Polygon";
        }).map((element) => {
          return element["geometry"]["coordinates"];
        })[0];
      });

      // Fetch all current feature's siblings.
      // let urn = this.worldService.currentUrn;
      let relatedItemsList: RelatedItemsInterface = [];
      let relatedItemsPromise = this.namService.getObjectByUrnAndRelatedItems(parentUrn).pipe(take(1)).toPromise();

      await relatedItemsPromise.then(
        ({ response, relatedItems }) => {
          relatedItemsList = relatedItems;
        },
        (error) => {
          console.log('ERROR', error);
          relatedItemsList = [];
        }
      );

      this.worldRelatedUrns = [];
      for(let item of relatedItemsList){
        if(this.featureUrn != item.urn) {
          this.worldRelatedUrns.push(item.urn);
        }
      }
    
      // Build related geometries
      let relatedGeometriesPromise = this.namService.getSelectByUrns(this.worldRelatedUrns)
      .pipe(take(1)).toPromise();
      this.relatedPolygons = [];

      await relatedGeometriesPromise.then( data => {
        this.relatedPolygons = data.result.features.filter((element) => {
          return element["geometry"]["type"] == "Polygon";
        }).map((element) => {
          return element["geometry"]["coordinates"];
        })
      })

      // Validate geometry.
      this.validateCurrentGeometry();
      
    }
    
  }
}
